import { useCallback, useEffect, useState } from "react";
import { AdvertisementTypeEnum } from "@app/types/enums";
import Button from "@components/AccessibleComponents/Button";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { adSlotSizes, adTargetings } from "@util/constant";
import cx from "classnames";
import Cookies, { CookieAttributes } from "js-cookie";

import { useAdvertLoaded } from "./helpers";

const ID = "dfp-ad-prestitial";
const TOPOVERLAY_VALIDITY = 4;
const TOPOVERLAYCLASS = "overlayWhite";

export type PrestitialAdProps = {
  rootClassName?: string;
  slot: string;
  topOverlayImpressions?: number;
  adBlockDetected?: boolean;
};

/**
 * This component renders an Prestitial ad.
 * @param rootClassName - The root class name.
 * @param slot - The ad slot.
 * @see https://developers.google.com/doubleclick-gpt/reference
 * @returns A Prestitial ad component. If the script is not ready, it returns error. If the script is ready, it returns the ad.
 */
export default function PrestitialAd({
  rootClassName,
  slot,
  topOverlayImpressions = 3,
  adBlockDetected = false,
}: PrestitialAdProps) {
  const [locked, setLocked] = useState(true);
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);

  const topoverlayDisplayedCookie = Cookies.get("topoverlayDisplayed");
  const topOverlayImpressionsServedCookie = Cookies.get(
    "topOverlayImpressionsServed"
  );
  const dfp_preview_ids = Cookies.get("dfp_preview_ids");
  const mySphUserTypeCookie =
    Cookies.get("mySPHUserType") || OKTAUserTypeEnum.ANONYMOUS;
  const hasDfpPreview =
    dfp_preview_ids != undefined &&
    dfp_preview_ids.length != 0 &&
    dfp_preview_ids != "";
  let impressionsServed = topOverlayImpressionsServedCookie
    ? parseInt(topOverlayImpressionsServedCookie)
    : 0;

  const isAdLoaded = useAdvertLoaded(AdvertisementTypeEnum.PRESTITIAL, ID);
  const [error, setError] = useState("");
  const [adSlot, setAdSlot] =
    useState<ReturnType<typeof googletag.defineSlot>>();
  const adSlotSize = adSlotSizes.prestitial;
  const adTarget = adTargetings.prestitial;

  const closetopoverlay = (event: MessageEvent) => {
    if (
      event.data !== "close" &&
      event.data !== "bz-interstitial-close" &&
      event.data.detail !== "close"
    )
      return;

    console.log("[pres] closetopoverlay : setLocked false"); // eslint-disable-line no-console
    setLocked(false);
  };

  const slotRenderEnded = (event: googletag.events.SlotRenderEndedEvent) => {
    if (event.slot.getSlotElementId() !== ID) return;
    if (event.isEmpty) {
      console.log("[pres] slotRenderEnded : setLocked false"); // eslint-disable-line no-console
      setLocked(false);
    }
  };

  const handleDisplayAd = useCallback(() => {
    try {
      window.googletag = window.googletag || { cmd: [] };
      googletag.cmd.push(function () {
        googletag
          .pubads()
          .getSlots()
          .forEach(function (_slot) {
            if (_slot.getSlotElementId()) {
              if (_slot.getSlotElementId() === ID) {
                googletag.destroySlots([_slot]);
              }
            }
          });

        if (adSlotSize) {
          const _adSlot = googletag.defineSlot(slot, adSlotSize, ID);

          if (_adSlot) {
            window.googletag.slots["prestitial"] = _adSlot;
            _adSlot.addService(googletag.pubads());

            adTarget?.forEach(({ key, value }) => {
              _adSlot.setTargeting(key, value);
            });

            googletag.pubads().enableSingleRequest();
            window.addEventListener("message", closetopoverlay, false);
            googletag.pubads().disableInitialLoad();
            googletag.enableServices();
            // googletag.display(ID);

            googletag
              .pubads()
              .addEventListener("slotRenderEnded", slotRenderEnded);

            // Store the ad slot for later use.
            setAdSlot(_adSlot);
            window.show_overlay = 1;
          }
        }
      });
    } catch (_error: unknown) {
      setError("Please refresh");
    }
  }, [slot, adSlotSize, adTarget]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof slot === "undefined") return;
    if (typeof adSlot !== "undefined") return;
    if (impressionsServed >= topOverlayImpressions) return;
    if (typeof topoverlayDisplayedCookie !== "undefined" && !hasDfpPreview)
      return;
    if (OKTAUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER) return;
    if (mySphUserTypeCookie === OKTAUserTypeEnum.SUBSCRIBER) return;
    if (adBlockDetected) return;

    console.log("[pres] handleDisplayAd"); // eslint-disable-line no-console
    window.show_overlay = 0;
    handleDisplayAd();
  }, [
    slot,
    handleDisplayAd,
    adSlot,
    impressionsServed,
    topOverlayImpressions,
    topoverlayDisplayedCookie,
    hasDfpPreview,
    OKTAUserInfo?.usertype,
    mySphUserTypeCookie,
    adBlockDetected,
  ]);

  useEffect(() => {
    if (typeof topoverlayDisplayedCookie === "undefined") return;
    if (hasDfpPreview) return;

    console.log("[pres] topoverlayDisplayedCookie : setLocked false"); // eslint-disable-line no-console
    setLocked(false);
  }, [hasDfpPreview, topoverlayDisplayedCookie]);

  useEffect(() => {
    if (OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER) return;

    console.log("[pres] subscriber : setLocked false"); // eslint-disable-line no-console
    setLocked(false);
  }, [isAdLoaded, setLocked, OKTAUserInfo?.usertype]);

  useEffect(() => {
    return () => {
      console.log("[pres] unmount destroySlots"); // eslint-disable-line no-console
      if (window?.googletag && window?.googletag?.apiReady && adSlot) {
        googletag.cmd.push(function () {
          googletag.destroySlots([adSlot]);
        });
      }
    };
  }, [adSlot]);

  useEffect(() => {
    return () => {
      console.log("[pres] unmount reloadScrollBars"); // eslint-disable-line no-console
      if (typeof document === "undefined") return;

      const html = document.documentElement;
      const { body } = document;

      html.style.overflowY = "visible";
      body.style.overflowY = "visible";
    };
  }, []);

  // UseEffect to handle enabling/disabling of scrollbars depending if ad is loaded.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof window.canRunAds === "undefined") return;
    if (window.canRunAds) return;

    console.log("[pres] canRunAds : setLocked false"); // eslint-disable-line no-console
    setLocked(false);
  }, [isAdLoaded, setLocked]);

  useEffect(() => {
    if (!isAdLoaded) return;

    if (impressionsServed < topOverlayImpressions) {
      const expiry = new Date();
      expiry.setTime(expiry.getTime() + TOPOVERLAY_VALIDITY * 60 * 60 * 1000);
      const cookieOptions: CookieAttributes = {
        path: "/",
        expires: expiry,
      };

      impressionsServed++;

      if (impressionsServed === topOverlayImpressions) {
        Cookies.set("topOverlayImpressionsServed", "0", cookieOptions);
        Cookies.set("topoverlayDisplayed", "yes", cookieOptions);
        window.show_overlay = 0;
      } else {
        Cookies.set(
          "topOverlayImpressionsServed",
          impressionsServed.toString(),
          cookieOptions
        );
        window.show_overlay = 1;
      }
    }
  }, [
    isAdLoaded,
    impressionsServed,
    topoverlayDisplayedCookie,
    topOverlayImpressions,
  ]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const html = document.documentElement;
    const { body } = document;
    const overflow = locked ? "hidden" : "visible";

    html.style.overflowY = overflow;
    body.style.overflowY = overflow;
  }, [locked]);

  console.log("[pres] prestitialad component"); // eslint-disable-line no-console
  console.log("[pres] locked", locked); // eslint-disable-line no-console
  console.log("[pres] oktauserinfo", OKTAUserInfo?.usertype); // eslint-disable-line no-console
  console.log("[pres] mySphUserTypeCookie", mySphUserTypeCookie); // eslint-disable-line no-console
  console.log("[pres] adBlockDetected", adBlockDetected); // eslint-disable-line no-console
  console.log("[pres] -------------"); // eslint-disable-line no-console

  return (
    <>
      <div
        id="MyPageOverlay"
        {...(locked ? { className: TOPOVERLAYCLASS } : undefined)}
      ></div>

      <div
        className={cx(
          "container flex w-full flex-row items-center justify-center",
          rootClassName,
          { invisible: !isAdLoaded }
        )}
      >
        {!error ? <Button id={ID}></Button> : error}
      </div>
    </>
  );
}
